import React, { useState, useEffect } from 'react';
import { getProducts, createProduct, updateProduct, deleteProduct, importProducts, getProductTemplate, checkProductDependencies, deleteProductCascade, getProductCategories, updateSettings, bulkUpdateProductCategories } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { canEdit } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Loader2, Package, Upload, Download, AlertTriangle, Layers, FileText, Scissors, Filter, Settings, ChevronDown, ChevronLeft, ChevronRight, X, Tag } from 'lucide-react';

// Default categories (fallback)
const DEFAULT_CATEGORIES = [
  'Cheese - Blue',
  'Cheese - Soft',
  'Cheese - Hard',
  'Cheese - Fresh',
  'Dairy',
  'Packaging',
  'Other'
];

const emptyProduct = {
  sku: '',
  name: '',
  category: '',
  categories: [],  // New: multiple categories
  pack_size: '',
  units_per_case: '1',
  weight_per_unit: '',
  storage_requirements: '',
};

export default function Products() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  // Categories management
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [savingCategories, setSavingCategories] = useState(false);

  // Bulk selection
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Bulk category assignment
  const [bulkCategoryDialogOpen, setBulkCategoryDialogOpen] = useState(false);
  const [bulkCategoryAction, setBulkCategoryAction] = useState('add'); // 'set', 'add', 'remove'
  const [bulkSelectedCategories, setBulkSelectedCategories] = useState([]);
  const [bulkCategoryUpdating, setBulkCategoryUpdating] = useState(false);

  // Dependency check dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [dependencies, setDependencies] = useState(null);
  const [checkingDependencies, setCheckingDependencies] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await getProductCategories();
      setCategories(response.data || DEFAULT_CATEGORIES);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setCategories(DEFAULT_CATEGORIES);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    if (categories.includes(newCategory.trim())) {
      toast.error('Category already exists');
      return;
    }
    
    const updatedCategories = [...categories, newCategory.trim()];
    setSavingCategories(true);
    try {
      await updateSettings({ product_categories: updatedCategories });
      setCategories(updatedCategories);
      setNewCategory('');
      toast.success('Category added');
    } catch (error) {
      toast.error('Failed to add category');
    } finally {
      setSavingCategories(false);
    }
  };

  const handleRemoveCategory = async (categoryToRemove) => {
    // Check if any products use this category
    const productsUsingCategory = products.filter(p => p.category === categoryToRemove);
    if (productsUsingCategory.length > 0) {
      toast.error(`Cannot remove: ${productsUsingCategory.length} product(s) use this category`);
      return;
    }
    
    const updatedCategories = categories.filter(c => c !== categoryToRemove);
    setSavingCategories(true);
    try {
      await updateSettings({ product_categories: updatedCategories });
      setCategories(updatedCategories);
      toast.success('Category removed');
    } catch (error) {
      toast.error('Failed to remove category');
    } finally {
      setSavingCategories(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await getProducts({ search });
      setProducts(response.data || []);
    } catch (error) {
      console.error('Failed to load products:', error.response?.status, error.message);
      // Don't show error if it's just empty data
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (error.response?.status >= 500) {
        toast.error('Server error loading products. Please try again.');
      } else if (!error.response) {
        toast.error('Network error. Please check your connection.');
      } else {
        toast.error('Failed to load products');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      loadProducts();
    }, 300);
    return () => clearTimeout(debounce);
  }, [search]);

  // Filter products by category
  const filteredProducts = products.filter(p => {
    if (categoryFilter === 'all') return true;
    const productCategories = p.categories?.length > 0 ? p.categories : (p.category ? [p.category] : []);
    if (categoryFilter === 'uncategorized') return productCategories.length === 0;
    return productCategories.includes(categoryFilter);
  });

  // Pagination
  const totalPages = pageSize === 'all' ? 1 : Math.ceil(filteredProducts.length / pageSize);
  const paginatedProducts = React.useMemo(() => {
    if (pageSize === 'all') return filteredProducts;
    const start = (currentPage - 1) * pageSize;
    return filteredProducts.slice(start, start + pageSize);
  }, [filteredProducts, currentPage, pageSize]);

  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, search]);

  // Get unique categories from products (merge with settings categories)
  const existingCategories = [...new Set(products.flatMap(p => {
    const cats = p.categories?.length > 0 ? p.categories : (p.category ? [p.category] : []);
    return cats;
  }).filter(Boolean))];
  const allCategories = [...new Set([...categories, ...existingCategories])].sort();

  // Bulk selection handlers
  const toggleSelectAll = () => {
    if (selectedProducts.size === paginatedProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(paginatedProducts.map(p => p.id)));
    }
  };

  const toggleSelectProduct = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.size === 0) return;
    
    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const productId of selectedProducts) {
      try {
        await deleteProduct(productId);
        successCount++;
      } catch (error) {
        failCount++;
      }
    }
    
    setBulkDeleting(false);
    setBulkDeleteDialogOpen(false);
    setSelectedProducts(new Set());
    
    if (successCount > 0) {
      toast.success(`Deleted ${successCount} product${successCount > 1 ? 's' : ''}`);
    }
    if (failCount > 0) {
      toast.error(`Failed to delete ${failCount} product${failCount > 1 ? 's' : ''} (may have dependencies)`);
    }
    
    loadProducts();
  };

  const handleBulkCategoryUpdate = async () => {
    if (selectedProducts.size === 0 || bulkSelectedCategories.length === 0) {
      toast.error('Please select products and categories');
      return;
    }
    
    setBulkCategoryUpdating(true);
    try {
      const response = await bulkUpdateProductCategories(
        Array.from(selectedProducts),
        bulkSelectedCategories,
        bulkCategoryAction
      );
      toast.success(response.data.message);
      setBulkCategoryDialogOpen(false);
      setSelectedProducts(new Set());
      setBulkSelectedCategories([]);
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update categories');
    } finally {
      setBulkCategoryUpdating(false);
    }
  };

  const toggleBulkCategory = (category) => {
    setBulkSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const openCreateDialog = () => {
    setEditingProduct(null);
    setFormData(emptyProduct);
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      sku: product.sku || '',
      name: product.name || '',
      category: product.category || '',
      pack_size: product.pack_size || '',
      units_per_case: product.units_per_case?.toString() || '1',
      weight_per_unit: product.weight_per_unit?.toString() || '',
      storage_requirements: product.storage_requirements || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.sku || !formData.name) {
      toast.error('SKU and name are required');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        units_per_case: parseFloat(formData.units_per_case) || 1,
        weight_per_unit: formData.weight_per_unit ? parseFloat(formData.weight_per_unit) : null,
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, data);
        toast.success('Product updated');
      } else {
        await createProduct(data);
        toast.success('Product created');
      }
      setDialogOpen(false);
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    // First check dependencies
    setProductToDelete(product);
    setCheckingDependencies(true);
    setDeleteDialogOpen(true);
    
    try {
      const response = await checkProductDependencies(product.id);
      setDependencies(response.data);
    } catch (error) {
      toast.error('Failed to check dependencies');
      setDeleteDialogOpen(false);
    } finally {
      setCheckingDependencies(false);
    }
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    
    setDeleting(true);
    try {
      // Check if there are transformations (blocks deletion)
      if (dependencies?.transformation_count > 0) {
        toast.error('Cannot delete - product has transformations. Delete transformations first.');
        return;
      }
      
      if (dependencies?.future_dispatch_count > 0 || dependencies?.batch_count > 0) {
        // Use cascade delete
        await deleteProductCascade(productToDelete.id, {
          delete_future_dispatches: true,
          confirm: true
        });
        toast.success(`Product deleted. ${dependencies.batch_count} batch(es) and ${dependencies.future_lines_count} dispatch line(s) removed.`);
      } else {
        // Simple delete
        await deleteProduct(productToDelete.id);
        toast.success('Product deactivated');
      }
      setDeleteDialogOpen(false);
      setProductToDelete(null);
      setDependencies(null);
      loadProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    } finally {
      setDeleting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const response = await importProducts(file);
      const { imported, skipped, errors } = response.data;
      toast.success(`Imported ${imported} products. Skipped ${skipped}. Errors: ${errors.length}`);
      if (errors.length > 0) {
        console.error('Import errors:', errors);
      }
      setImportDialogOpen(false);
      loadProducts();
    } catch (error) {
      toast.error('Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    window.open(getProductTemplate(), '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-2 flex-1 w-full">
          <div className="relative flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="search-products"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="uncategorized">Uncategorized</SelectItem>
              {allCategories.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selectedProducts.size > 0 && canEdit(user?.role) && (
            <>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setBulkSelectedCategories([]);
                  setBulkCategoryAction('add');
                  setBulkCategoryDialogOpen(true);
                }}
                className="sm:h-10"
              >
                <Tag className="w-4 h-4 mr-1 sm:mr-2" />
                Assign Categories ({selectedProducts.size})
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setBulkSelectedCategories([]);
                  setBulkCategoryAction('remove');
                  setBulkCategoryDialogOpen(true);
                }}
                className="sm:h-10 text-orange-600 border-orange-300 hover:bg-orange-50"
                data-testid="btn-unassign-categories"
              >
                <X className="w-4 h-4 mr-1 sm:mr-2" />
                Unassign Categories ({selectedProducts.size})
              </Button>
              <Button 
                variant="destructive" 
                size="sm" 
                onClick={() => setBulkDeleteDialogOpen(true)}
                className="sm:h-10"
              >
                <Trash2 className="w-4 h-4 mr-1 sm:mr-2" />
                Delete {selectedProducts.size} Selected
              </Button>
            </>
          )}
          {canEdit(user?.role) && (
            <>
              <Button variant="outline" onClick={() => setImportDialogOpen(true)} size="sm" className="sm:h-10" data-testid="btn-import">
                <Upload className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Import </span>Excel
              </Button>
              <Button onClick={openCreateDialog} size="sm" className="sm:h-10" data-testid="btn-add-product">
              <Plus className="w-4 h-4 mr-1 sm:mr-2" />
              Add Product
            </Button>
            </>
          )}
        </div>
      </div>

      {/* Category Management */}
      <Collapsible open={categoriesOpen} onOpenChange={setCategoriesOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-primary" />
                  <CardTitle className="text-sm font-medium">Manage Categories</CardTitle>
                  <Badge variant="secondary" className="text-xs">{categories.length}</Badge>
                </div>
                <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${categoriesOpen ? 'rotate-180' : ''}`} />
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="pt-0">
              <div className="space-y-4">
                {/* Add new category */}
                <div className="flex gap-2">
                  <Input
                    placeholder="New category name..."
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                    className="max-w-xs"
                    data-testid="input-new-category"
                  />
                  <Button 
                    onClick={handleAddCategory} 
                    disabled={savingCategories || !newCategory.trim()}
                    size="sm"
                    data-testid="btn-add-category"
                  >
                    {savingCategories ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* Category list */}
                <div className="flex flex-wrap gap-2">
                  {categories.map(cat => {
                    const productCount = products.filter(p => p.category === cat).length;
                    return (
                      <Badge 
                        key={cat} 
                        variant="outline" 
                        className="py-1.5 px-3 flex items-center gap-2"
                      >
                        <span>{cat}</span>
                        <span className="text-xs text-muted-foreground">({productCount})</span>
                        {productCount === 0 && (
                          <button
                            onClick={() => handleRemoveCategory(cat)}
                            className="ml-1 hover:text-destructive"
                            title="Remove category"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </Badge>
                    );
                  })}
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Categories with products assigned cannot be removed. Reassign products first.
                </p>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Results count and pagination controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 text-sm text-muted-foreground">
        <span>
          {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
          {pageSize !== 'all' && totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-xs">Show:</span>
          <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(v === 'all' ? 'all' : parseInt(v)); setCurrentPage(1); }}>
            <SelectTrigger className="w-20 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          {pageSize !== 'all' && totalPages > 1 && (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">{currentPage}</span>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Products Table */}
      {paginatedProducts.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    {canEdit(user?.role) && (
                      <th className="w-10 px-3 py-3">
                        <Checkbox
                          checked={selectedProducts.size === paginatedProducts.length && paginatedProducts.length > 0}
                          onCheckedChange={toggleSelectAll}
                          aria-label="Select all on page"
                        />
                      </th>
                    )}
                    <th className="text-left px-3 sm:px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">SKU</th>
                    <th className="text-left px-3 sm:px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Name</th>
                    <th className="text-left px-3 sm:px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Category</th>
                    <th className="text-right px-3 sm:px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground hidden md:table-cell">Units/Case</th>
                    <th className="text-right px-3 sm:px-4 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground hidden md:table-cell">Weight/Unit</th>
                    <th className="w-20 sm:w-24"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginatedProducts.map((product) => (
                    <tr 
                      key={product.id} 
                      className={`hover:bg-muted/30 ${selectedProducts.has(product.id) ? 'bg-primary/5' : ''}`}
                      data-testid={`product-row-${product.id}`}
                    >
                      {canEdit(user?.role) && (
                        <td className="px-3 py-2">
                          <Checkbox
                            checked={selectedProducts.has(product.id)}
                            onCheckedChange={() => toggleSelectProduct(product.id)}
                            aria-label={`Select ${product.name}`}
                          />
                        </td>
                      )}
                      <td className="px-3 sm:px-4 py-2 sm:py-3 font-mono text-xs sm:text-sm">{product.sku}</td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 font-medium text-sm">{product.name}</td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-sm hidden sm:table-cell">
                        {(() => {
                          const cats = product.categories?.length > 0 ? product.categories : (product.category ? [product.category] : []);
                          if (cats.length === 0) {
                            return <span className="text-muted-foreground text-xs">-</span>;
                          }
                          return (
                            <div className="flex flex-wrap gap-1">
                              {cats.slice(0, 2).map(cat => (
                                <span key={cat} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  {cat}
                                </span>
                              ))}
                              {cats.length > 2 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                  +{cats.length - 2}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-right font-mono text-sm hidden md:table-cell">{product.units_per_case}</td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3 text-right font-mono text-sm hidden md:table-cell">
                        {product.weight_per_unit ? `${product.weight_per_unit} kg` : '-'}
                      </td>
                      <td className="px-3 sm:px-4 py-2 sm:py-3">
                        {canEdit(user?.role) && (
                          <div className="flex gap-1 justify-end">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(product)} className="h-8 w-8 p-0">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-destructive h-8 w-8 p-0" onClick={() => handleDelete(product)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-8 sm:p-12 text-center">
            <Package className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-base sm:text-lg font-medium mb-2">No products found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {search ? 'Try adjusting your search' : 'Get started by adding your first product'}
            </p>
            {!search && canEdit(user?.role) && (
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button onClick={openCreateDialog} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Product
                </Button>
                <Button variant="outline" onClick={() => setImportDialogOpen(true)} size="sm">
                  <Upload className="w-4 h-4 mr-2" />
                  Import from Excel
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>SKU *</Label>
                <Input
                  value={formData.sku}
                  onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                  className="font-mono"
                  data-testid="input-product-sku"
                />
              </div>
              <div className="space-y-2">
                <Label>Categories</Label>
                <div className="border rounded-lg p-2 max-h-32 overflow-y-auto">
                  {allCategories.length > 0 ? (
                    <div className="space-y-1">
                      {allCategories.map(cat => {
                        const isSelected = (formData.categories || []).includes(cat) || formData.category === cat;
                        return (
                          <div 
                            key={cat} 
                            className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer hover:bg-muted/50 ${isSelected ? 'bg-primary/10' : ''}`}
                            onClick={() => {
                              const currentCats = formData.categories || (formData.category ? [formData.category] : []);
                              const newCats = currentCats.includes(cat)
                                ? currentCats.filter(c => c !== cat)
                                : [...currentCats, cat];
                              setFormData({ ...formData, categories: newCats, category: newCats[0] || '' });
                            }}
                          >
                            <Checkbox checked={isSelected} />
                            <span className="text-sm">{cat}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground text-center py-2">No categories defined</p>
                  )}
                </div>
                {(formData.categories?.length > 0 || formData.category) && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(formData.categories?.length > 0 ? formData.categories : [formData.category].filter(Boolean)).map(cat => (
                      <Badge key={cat} variant="secondary" className="text-xs">{cat}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Product Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="input-product-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pack Size</Label>
                <Input
                  value={formData.pack_size}
                  onChange={(e) => setFormData({ ...formData, pack_size: e.target.value })}
                  placeholder="e.g., 200g"
                />
              </div>
              <div className="space-y-2">
                <Label>Units per Case</Label>
                <Input
                  type="number"
                  value={formData.units_per_case}
                  onChange={(e) => setFormData({ ...formData, units_per_case: e.target.value })}
                  className="font-mono"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Weight per Unit (kg)</Label>
              <Input
                type="number"
                step="0.001"
                value={formData.weight_per_unit}
                onChange={(e) => setFormData({ ...formData, weight_per_unit: e.target.value })}
                className="font-mono"
                placeholder="e.g., 0.200"
              />
              <p className="text-xs text-muted-foreground">
                Used to calculate weight in dispatches
              </p>
            </div>

            <div className="space-y-2">
              <Label>Storage Requirements</Label>
              <Input
                value={formData.storage_requirements}
                onChange={(e) => setFormData({ ...formData, storage_requirements: e.target.value })}
                placeholder="e.g., Keep refrigerated at 2-8°C"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="btn-save-product">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingProduct ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Products from Excel</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload an Excel file with product data. Required columns: SKU, Name
            </p>
            <p className="text-sm text-muted-foreground">
              Optional columns: Format, Pack Size, Units Per Case, Weight Per Unit, Storage
            </p>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </div>

            <div className="border-t pt-4">
              <Label className="mb-2 block">Upload Excel File</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImport}
                disabled={importing}
                data-testid="input-import-file"
              />
            </div>
            
            {importing && (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Importing...</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog with Dependencies */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setDeleteDialogOpen(false);
          setProductToDelete(null);
          setDependencies(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Product
            </DialogTitle>
            <DialogDescription>
              {productToDelete?.name} ({productToDelete?.sku})
            </DialogDescription>
          </DialogHeader>
          
          {checkingDependencies ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Checking dependencies...</span>
            </div>
          ) : dependencies ? (
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">{dependencies.message}</p>
              
              {dependencies.transformation_count > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                    <Scissors className="w-4 h-4" />
                    {dependencies.transformation_count} Transformation(s) - BLOCKS DELETION
                  </div>
                  <p className="text-sm text-red-600">
                    This product has transformation records. Delete transformations first.
                  </p>
                </div>
              )}
              
              {dependencies.batch_count > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                    <Layers className="w-4 h-4" />
                    {dependencies.batch_count} Batch(es) with {dependencies.total_stock} units stock - Will be deleted
                  </div>
                  <div className="max-h-24 overflow-y-auto text-sm text-amber-600 space-y-1">
                    {dependencies.batches.slice(0, 5).map(b => (
                      <div key={b.id} className="font-mono">
                        {b.batch_code} - {b.quantity_available?.toFixed(0)} avail ({b.qc_status})
                      </div>
                    ))}
                    {dependencies.batch_count > 5 && (
                      <div className="text-xs">...and {dependencies.batch_count - 5} more</div>
                    )}
                  </div>
                </div>
              )}
              
              {dependencies.planned_count > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-amber-700 font-medium mb-2">
                    <FileText className="w-4 h-4" />
                    {dependencies.planned_count} Planned Order(s) from Weekly Planner - Lines will be removed
                  </div>
                  <div className="max-h-32 overflow-y-auto text-sm text-amber-600 space-y-1.5">
                    {dependencies.planned_orders?.slice(0, 5).map(d => (
                      <div key={d.id} className="bg-amber-100/50 rounded px-2 py-1">
                        <div className="flex justify-between">
                          <span className="font-mono">{d.dispatch_number}</span>
                          <span className="text-xs">{d.dispatch_date}</span>
                        </div>
                        <div className="text-xs text-amber-500">
                          {d.customer_name} • {d.affected_lines} line(s) affected
                        </div>
                      </div>
                    ))}
                    {dependencies.planned_count > 5 && (
                      <div className="text-xs text-center pt-1">...and {dependencies.planned_count - 5} more orders</div>
                    )}
                  </div>
                  <p className="text-xs text-amber-500 mt-2 pt-2 border-t border-amber-200">
                    Total: {dependencies.planned_lines_count || 0} line item(s) will be removed
                  </p>
                </div>
              )}
              
              {dependencies.ready_count > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-orange-700 font-medium mb-2">
                    <Package className="w-4 h-4" />
                    {dependencies.ready_count} Ready Order(s) - Lines will be removed
                  </div>
                  <div className="max-h-32 overflow-y-auto text-sm text-orange-600 space-y-1.5">
                    {dependencies.ready_orders?.slice(0, 5).map(d => (
                      <div key={d.id} className="bg-orange-100/50 rounded px-2 py-1">
                        <div className="flex justify-between">
                          <span className="font-mono">{d.dispatch_number}</span>
                          <span className="text-xs">{d.dispatch_date}</span>
                        </div>
                        <div className="text-xs text-orange-500">
                          {d.customer_name} • {d.affected_lines} line(s) affected
                        </div>
                      </div>
                    ))}
                    {dependencies.ready_count > 5 && (
                      <div className="text-xs text-center pt-1">...and {dependencies.ready_count - 5} more orders</div>
                    )}
                  </div>
                  <p className="text-xs text-orange-500 mt-2 pt-2 border-t border-orange-200">
                    Total: {dependencies.ready_lines_count || 0} line item(s) will be removed
                  </p>
                </div>
              )}
              
              {dependencies.past_dispatch_count > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-blue-700 font-medium mb-2">
                    <Package className="w-4 h-4" />
                    {dependencies.past_dispatch_count} Past Dispatch(es) - Will be preserved
                  </div>
                  <p className="text-sm text-blue-600">
                    Product name will be retained in historical records.
                  </p>
                </div>
              )}
              
              {!dependencies.has_dependencies && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700">
                  No dependencies found. Safe to delete.
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              disabled={checkingDependencies || deleting || dependencies?.transformation_count > 0}
            >
              {deleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
              ) : dependencies?.transformation_count > 0 ? (
                <>Cannot Delete</>
              ) : dependencies?.batch_count > 0 || dependencies?.future_dispatch_count > 0 ? (
                <><Trash2 className="w-4 h-4 mr-2" /> Delete Product & Dependencies</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Delete Product</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete {selectedProducts.size} Products
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete these products? This action cannot be undone.
              Products with batches, dispatches, or transformations may fail to delete.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
              {filteredProducts.filter(p => selectedProducts.has(p.id)).map(product => (
                <div key={product.id} className="flex items-center justify-between text-sm py-1 px-2 hover:bg-muted/50 rounded">
                  <div>
                    <span className="font-mono text-xs text-muted-foreground">{product.sku}</span>
                    <span className="mx-2">-</span>
                    <span className="font-medium">{product.name}</span>
                  </div>
                  {product.category && (
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                      {product.category}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)} disabled={bulkDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleting}>
              {bulkDeleting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Delete {selectedProducts.size} Products</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Category Assignment Dialog */}
      <Dialog open={bulkCategoryDialogOpen} onOpenChange={setBulkCategoryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {bulkCategoryAction === 'remove' ? (
                <X className="w-5 h-5 text-orange-600" />
              ) : (
                <Tag className="w-5 h-5 text-primary" />
              )}
              {bulkCategoryAction === 'remove' 
                ? `Unassign Categories from ${selectedProducts.size} Products`
                : `Assign Categories to ${selectedProducts.size} Products`}
            </DialogTitle>
            <DialogDescription>
              {bulkCategoryAction === 'remove' 
                ? 'Select categories to remove from the selected products.'
                : 'Select categories and choose how to apply them.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Action Type - Only show if not in remove mode */}
            {bulkCategoryAction !== 'remove' && (
              <div className="space-y-2">
                <Label>Action</Label>
                <Select value={bulkCategoryAction} onValueChange={setBulkCategoryAction}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="add">Add to existing categories</SelectItem>
                    <SelectItem value="set">Replace all categories</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Category Selection */}
            <div className="space-y-2">
              <Label>{bulkCategoryAction === 'remove' ? 'Categories to Remove' : 'Select Categories'}</Label>
              <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                {allCategories.map(cat => (
                  <div 
                    key={cat} 
                    className={`flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-muted/50 ${
                      bulkSelectedCategories.includes(cat) 
                        ? bulkCategoryAction === 'remove' 
                          ? 'bg-orange-100 border border-orange-300' 
                          : 'bg-primary/10 border border-primary/30' 
                        : ''
                    }`}
                    onClick={() => toggleBulkCategory(cat)}
                  >
                    <Checkbox 
                      checked={bulkSelectedCategories.includes(cat)}
                      onCheckedChange={() => toggleBulkCategory(cat)}
                    />
                    <span className="text-sm">{cat}</span>
                  </div>
                ))}
                {allCategories.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No categories defined. Add categories in the "Manage Categories" section above.
                  </p>
                )}
              </div>
            </div>
            
            {/* Selected Products Preview */}
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Selected Products:</Label>
              <div className="border rounded-lg p-2 max-h-24 overflow-y-auto">
                {filteredProducts.filter(p => selectedProducts.has(p.id)).slice(0, 5).map(product => (
                  <div key={product.id} className="text-xs text-muted-foreground truncate">
                    {product.name}
                  </div>
                ))}
                {selectedProducts.size > 5 && (
                  <div className="text-xs text-muted-foreground">...and {selectedProducts.size - 5} more</div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkCategoryDialogOpen(false)} disabled={bulkCategoryUpdating}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkCategoryUpdate} 
              disabled={bulkCategoryUpdating || bulkSelectedCategories.length === 0}
              className={bulkCategoryAction === 'remove' ? 'bg-orange-600 hover:bg-orange-700' : ''}
            >
              {bulkCategoryUpdating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</>
              ) : bulkCategoryAction === 'remove' ? (
                <><X className="w-4 h-4 mr-2" /> Remove from {selectedProducts.size} Products</>
              ) : (
                <><Tag className="w-4 h-4 mr-2" /> Apply to {selectedProducts.size} Products</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
